import { useState, useCallback } from 'react';

/**
 * Persists filter state in sessionStorage so it survives page refreshes and
 * back-navigation. Each call site must provide a unique `storageKey`.
 *
 * @param {string} storageKey  - Unique key used in sessionStorage.
 * @param {*}      defaultValue - Value used when nothing is stored yet.
 * @returns {[any, Function, Function]} [filters, setFilters, resetFilters]
 */
const useFilterPersistence = (storageKey, defaultValue) => {
  const [filters, setFiltersState] = useState(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setFilters = useCallback((newFiltersOrUpdater) => {
    setFiltersState(prev => {
      const newFilters = typeof newFiltersOrUpdater === 'function'
        ? newFiltersOrUpdater(prev)
        : newFiltersOrUpdater;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(newFilters));
      } catch {
        // sessionStorage unavailable — degrade gracefully
      }
      return newFilters;
    });
  }, [storageKey]);

  const resetFilters = useCallback(() => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    setFiltersState(defaultValue);
  // defaultValue is intentionally excluded — callers pass a stable reference
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  return [filters, setFilters, resetFilters];
};

export default useFilterPersistence;
